"use client"

import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "./ui/button";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet"
import { LayoutDashboard, Target, ListChecks, Tags, Bot, LogOut, Menu, BookOpenCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

interface AppLayoutProps {
  children: React.ReactNode
}

export function AppLayout({ children }: AppLayoutProps) {
  const [open, setOpen] = React.useState(false)
  const { user, logout } = useAuth()

  const navigationItems = [
    { icon: LayoutDashboard, label: "Dashboard", href: "/dashboard" },
    { icon: ListChecks, label: "Daily Tasks", href: "/tasks" },
    { icon: Target, label: "Goals", href: "/goals" },
    { icon: Tags, label: "Categories", href: "/categories" },
    { icon: BookOpenCheck, label: "Habits", href: "/habits" },
  ];

  const botItem = { label: "Goal Planner Bot (coming soon)" };

  const SidebarContent = () => (
    <div className="flex h-full flex-col justify-between">
      <div className="space-y-4 py-4">
        <div className="px-3 py-2">
          <h2 className="mb-2 px-4 text-lg font-semibold">App Menu</h2>
          <div className="space-y-1">
            {navigationItems.map((item) => (
              <Button key={item.label} variant="ghost" className="w-full justify-start" asChild>
                <Link to={item.href}>
                  <item.icon className="mr-2 h-4 w-4" />
                  {item.label}
                </Link>
              </Button>
            ))}
            <div className="px-4 py-2 text-sm text-muted-foreground flex items-center">
              <Bot className="mr-2 h-4 w-4" />
              {botItem.label}
            </div>
          </div>
        </div>
      </div>
      <div className="border-t p-4">
        <div className="flex items-center gap-4">
          <Avatar>
            <AvatarFallback>
              {user?.email ? user.email[0].toUpperCase() : "?"}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="text-sm font-medium">
              {user?.email || "User"}
            </span>
          </div>
          <Button variant="outline" size="icon" className="ml-auto" onClick={logout}>
            <LogOut className="h-4 w-4" />
            <span className="sr-only">Log out</span>
          </Button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="flex min-h-screen flex-col">
      {/* Mobile Header */}
      <header className="sticky top-0 z-30 flex h-14 items-center gap-4 border-b bg-background px-4 md:hidden">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="md:hidden">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[240px] p-0">
            <SidebarContent />
          </SheetContent>
        </Sheet>
        <div className="font-semibold">Schwinghub</div>
      </header>

      <div className="flex flex-1">
        {/* Desktop Sidebar */}
        <aside className="fixed hidden h-screen w-[240px] border-r bg-muted md:block">
          <SidebarContent />
        </aside>

        {/* Main Content */}
        <main className="flex-1 md:ml-[240px]">
          {children}
        </main>
      </div>
    </div>
  )
}
